/**
 * Shift Swap & Coverage page — Sprint 19+.
 * SRS Open Item #3: Shift swap workflow with approval chain.
 *
 * Features:
 * - My Requests / Incoming tabs
 * - History tab with individual totals (who covered whom)
 * - "New Request" button: small, right-aligned, green accent
 * - Coverage requests integrated into the same page
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { isOfficerOrAbove } from '../lib/auth';
import { useSwaps } from '../hooks/useSwaps';
import { useMediaQuery } from '../hooks/useMediaQuery';
import type { SwapEntry, SwapCreatePayload } from '../hooks/useSwaps';
import { apiFetch } from '../lib/api';
import AppLayout from '../components/layout/AppLayout';
import {
  ArrowLeftRight,
  Plus,
  Send,
  Inbox,
  Check,
  X as XIcon,
  Clock,
  AlertCircle,
  ChevronRight,
  History,
  Users,
} from 'lucide-react';

// -- Types --

interface MemberOption {
  id: number;
  display_name: string;
  email: string;
  battalion_id: number | null;
}

type TabId = 'my' | 'incoming' | 'history';

// -- Status config --

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  pending: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)', label: 'Pending' },
  approved: { color: '#22C55E', bg: 'rgba(34, 197, 94, 0.12)', label: 'Approved' },
  rejected: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.12)', label: 'Rejected' },
  cancelled: { color: '#666666', bg: 'rgba(102, 102, 102, 0.12)', label: 'Cancelled' },
  completed: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)', label: 'Completed' },
};

// -- Helpers --

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T12:00:00Z').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// -- Component --

export default function ShiftSwap() {
  const { user } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const {
    mySwaps,
    incoming,
    history: swapHistory,
    loading,
    error,
    fetchMySwaps,
    fetchIncoming,
    fetchHistory,
    createSwap,
    cancelSwap,
    approveSwap,
    rejectSwap,
  } = useSwaps();

  const [tab, setTab] = useState<TabId>('my');
  const [showNewForm, setShowNewForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [historyView, setHistoryView] = useState<'list' | 'totals'>('list');

  // New swap form
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [targetId, setTargetId] = useState<string>('');
  const [requesterDate, setRequesterDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOfficer = isOfficerOrAbove(user);
  const currentMemberId = user?.member_id ? parseInt(user.member_id, 10) : null;

  useEffect(() => {
    if (tab === 'my') fetchMySwaps();
    if (tab === 'incoming') fetchIncoming();
    if (tab === 'history') fetchHistory();
  }, [tab, fetchMySwaps, fetchIncoming, fetchHistory]);

  useEffect(() => {
    if (showNewForm && members.length === 0) {
      apiFetch<{ data: MemberOption[] }>('/api/swaps/eligible-members')
        .then((res) => setMembers(res.data ?? []))
        .catch(() => setMembers([]));
    }
  }, [showNewForm, members.length]);

  // Compute swap totals by individual
  const swapTotals = useMemo(() => {
    if (!currentMemberId) return [];
    const totalsMap = new Map<string, { name: string; theyCoveredMe: number; iCoveredThem: number }>();

    for (const swap of swapHistory) {
      if (swap.status !== 'approved' && swap.status !== 'completed') continue;

      const isRequester = swap.requester_id === currentMemberId;
      const otherName = isRequester ? swap.target_name : swap.requester_name;
      const otherId = isRequester ? String(swap.target_id) : String(swap.requester_id);

      if (!totalsMap.has(otherId)) {
        totalsMap.set(otherId, { name: otherName, theyCoveredMe: 0, iCoveredThem: 0 });
      }
      const entry = totalsMap.get(otherId)!;
      if (isRequester) {
        // I requested, they covered my shift
        entry.theyCoveredMe += 1;
      } else {
        // They requested, I covered their shift
        entry.iCoveredThem += 1;
      }
    }

    return Array.from(totalsMap.entries())
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.theyCoveredMe + b.iCoveredThem - (a.theyCoveredMe + a.iCoveredThem));
  }, [swapHistory, currentMemberId]);

  const handleCreateSwap = useCallback(async () => {
    setActionError(null);
    setActionSuccess(null);

    if (!targetId || !requesterDate || !targetDate) {
      setActionError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload: SwapCreatePayload = {
        target_id: parseInt(targetId, 10),
        requester_date: requesterDate,
        target_date: targetDate,
        reason: reason || undefined,
      };
      await createSwap(payload);
      setActionSuccess('Swap request submitted successfully');
      setTargetId('');
      setRequesterDate('');
      setTargetDate('');
      setReason('');
      setTimeout(() => {
        setShowNewForm(false);
        setTab('my');
        setActionSuccess(null);
      }, 1500);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create swap request');
    } finally {
      setSubmitting(false);
    }
  }, [targetId, requesterDate, targetDate, reason, createSwap]);

  const handleAction = useCallback(
    async (action: 'cancel' | 'approve' | 'reject', swapId: number) => {
      setActionError(null);
      setActionSuccess(null);
      try {
        if (action === 'cancel') await cancelSwap(swapId);
        if (action === 'approve') await approveSwap(swapId);
        if (action === 'reject') await rejectSwap(swapId);
        setActionSuccess(
          `Swap ${action}${action === 'approve' ? 'd' : action === 'reject' ? 'ed' : 'led'} successfully`,
        );
        fetchMySwaps();
        fetchIncoming();
        setTimeout(() => setActionSuccess(null), 2000);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : `Failed to ${action} swap`);
      }
    },
    [cancelSwap, approveSwap, rejectSwap, fetchMySwaps, fetchIncoming],
  );

  const tabs: { id: TabId; label: string; icon: typeof Send; count?: number }[] = [
    { id: 'my', label: 'My Requests', icon: Send, count: mySwaps.filter((s) => s.status === 'pending').length },
    { id: 'incoming', label: 'Incoming', icon: Inbox, count: incoming.filter((s) => s.status === 'pending').length },
    { id: 'history', label: 'History', icon: History },
  ];

  return (
    <AppLayout title="Swaps & Coverage">
      <div className={`w-full ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Page header with New Request button */}
        <div className="flex items-center justify-between mt-4 mb-5">
          <div className="flex items-center gap-2.5">
            <ArrowLeftRight className="h-5 w-5 text-action-primary" />
            <h1 className="text-xl font-bold text-content-primary">Swaps & Coverage</h1>
          </div>
          <button
            type="button"
            onClick={() => setShowNewForm(!showNewForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold
                       text-white border-none rounded-lg cursor-pointer
                       hover:opacity-90 transition-opacity duration-150 shadow-sm"
            style={{ backgroundColor: '#00674F' }}
          >
            <Plus className="h-3.5 w-3.5" />
            New Request
          </button>
        </div>

        {/* New Request Form (collapsible) */}
        {showNewForm && (
          <div
            className={`bg-surface-raised rounded-xl border border-edge-default p-5 mb-5 ${isDesktop ? 'max-w-xl' : ''}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-content-primary">New Swap Request</h2>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="p-1 rounded-md text-content-tertiary hover:bg-surface-sunken cursor-pointer border-none bg-transparent"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <FormField label="Swap With">
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm bg-surface-base border border-edge-default rounded-lg text-content-primary outline-none box-border
                           focus:border-action-primary transition-colors duration-150"
                >
                  <option value="">Select a member...</option>
                  {members
                    .filter((m) => String(m.id) !== user?.member_id)
                    .map((m) => (
                      <option key={m.id} value={String(m.id)}>
                        {m.display_name}
                      </option>
                    ))}
                </select>
                {members.length === 0 && (
                  <>
                    <div className="text-xs text-content-tertiary mt-1">
                      Enter the member ID directly if the list doesn&apos;t load.
                    </div>
                    <input
                      type="number"
                      placeholder="Member ID"
                      value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm bg-surface-base border border-edge-default rounded-lg text-content-primary outline-none box-border mt-1.5
                               focus:border-action-primary transition-colors duration-150"
                    />
                  </>
                )}
              </FormField>

              <div className={isDesktop ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
                <FormField label="Your Shift Date (giving away)">
                  <input
                    type="date"
                    value={requesterDate}
                    onChange={(e) => setRequesterDate(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm bg-surface-base border border-edge-default rounded-lg text-content-primary outline-none box-border
                             focus:border-action-primary transition-colors duration-150"
                  />
                </FormField>

                <FormField label="Their Shift Date (taking)">
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm bg-surface-base border border-edge-default rounded-lg text-content-primary outline-none box-border
                             focus:border-action-primary transition-colors duration-150"
                  />
                </FormField>
              </div>

              <FormField label="Reason (optional)">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm bg-surface-base border border-edge-default rounded-lg text-content-primary outline-none box-border resize-y
                           focus:border-action-primary transition-colors duration-150"
                  placeholder="Why do you need this swap?"
                />
                <div className="text-[11px] text-content-tertiary mt-1 text-right">{reason.length}/500</div>
              </FormField>

              <button
                type="button"
                onClick={handleCreateSwap}
                disabled={submitting}
                className={`w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold
                  rounded-xl border-none transition-all duration-150
                  ${
                    submitting
                      ? 'bg-surface-sunken text-content-tertiary cursor-default'
                      : 'text-white cursor-pointer hover:opacity-90 shadow-sm'
                  }`}
                style={!submitting ? { backgroundColor: '#00674F' } : undefined}
              >
                <ArrowLeftRight className="h-4 w-4" />
                {submitting ? 'Submitting...' : 'Submit Swap Request'}
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-surface-sunken rounded-xl p-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-[13px] rounded-lg
                  cursor-pointer transition-all duration-150 border-none
                  ${
                    isActive
                      ? 'bg-surface-raised text-content-primary font-semibold shadow-sm'
                      : 'bg-transparent text-content-secondary hover:text-content-primary'
                  }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span>{t.label}</span>
                {t.count != null && t.count > 0 && (
                  <span className="ml-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-action-primary text-content-on-primary rounded-full px-1">
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Status messages */}
        {(error || actionError) && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 text-[13px] text-danger-400 bg-danger-500/10 border border-danger-500/30 rounded-xl">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error || actionError}
          </div>
        )}
        {actionSuccess && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 text-[13px] text-success-500 bg-success-500/10 border border-success-500/30 rounded-xl">
            <Check className="h-4 w-4 shrink-0" />
            {actionSuccess}
          </div>
        )}

        {/* Tab content */}
        <div>
          {tab === 'my' && (
            <>
              {loading && <div className="text-content-secondary text-[13px] text-center py-8">Loading...</div>}
              {!loading && mySwaps.length === 0 && (
                <EmptyState
                  message="No swap requests yet"
                  action="Create your first swap request"
                  onAction={() => setShowNewForm(true)}
                />
              )}
              <div className={isDesktop ? 'grid grid-cols-2 gap-3' : 'space-y-3'}>
                {mySwaps.map((swap) => (
                  <SwapCard
                    key={swap.id}
                    swap={swap}
                    isIncoming={false}
                    isOfficer={isOfficer}
                    onAction={handleAction}
                  />
                ))}
              </div>
            </>
          )}

          {tab === 'incoming' && (
            <>
              {loading && <div className="text-content-secondary text-[13px] text-center py-8">Loading...</div>}
              {!loading && incoming.length === 0 && <EmptyState message="No incoming swap requests" />}
              <div className={isDesktop ? 'grid grid-cols-2 gap-3' : 'space-y-3'}>
                {incoming.map((swap) => (
                  <SwapCard key={swap.id} swap={swap} isIncoming={true} isOfficer={isOfficer} onAction={handleAction} />
                ))}
              </div>
            </>
          )}

          {tab === 'history' && (
            <>
              {loading && <div className="text-content-secondary text-[13px] text-center py-8">Loading...</div>}

              {/* History sub-tabs: List vs Totals */}
              {!loading && (
                <>
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setHistoryView('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border cursor-pointer transition-colors duration-150
                        ${
                          historyView === 'list'
                            ? 'bg-action-primary/10 text-action-primary border-action-primary/30'
                            : 'bg-transparent text-content-secondary border-edge-default hover:bg-surface-sunken'
                        }`}
                    >
                      <Clock className="h-3.5 w-3.5" />
                      All Swaps
                    </button>
                    <button
                      type="button"
                      onClick={() => setHistoryView('totals')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border cursor-pointer transition-colors duration-150
                        ${
                          historyView === 'totals'
                            ? 'bg-action-primary/10 text-action-primary border-action-primary/30'
                            : 'bg-transparent text-content-secondary border-edge-default hover:bg-surface-sunken'
                        }`}
                    >
                      <Users className="h-3.5 w-3.5" />
                      Totals by Person
                    </button>
                  </div>

                  {historyView === 'list' && (
                    <>
                      {swapHistory.length === 0 && <EmptyState message="No swap history yet" />}
                      <div className={isDesktop ? 'grid grid-cols-2 gap-3' : 'space-y-3'}>
                        {swapHistory.map((swap) => (
                          <SwapCard
                            key={swap.id}
                            swap={swap}
                            isIncoming={swap.target_id === currentMemberId}
                            isOfficer={false}
                            onAction={() => {}}
                            isHistorical
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {historyView === 'totals' && (
                    <>
                      {swapTotals.length === 0 && <EmptyState message="No completed swaps to show totals" />}
                      <div className="space-y-2">
                        {swapTotals.map((entry) => (
                          <div
                            key={entry.id}
                            className="bg-surface-raised border border-edge-default rounded-xl p-4 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-action-primary/15 flex items-center justify-center shrink-0">
                                <Users className="h-4 w-4 text-action-primary" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-content-primary">{entry.name}</div>
                                <div className="text-[11px] text-content-tertiary">
                                  {entry.theyCoveredMe + entry.iCoveredThem} total swap
                                  {entry.theyCoveredMe + entry.iCoveredThem !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-4 text-center">
                              <div>
                                <div className="text-lg font-bold text-action-primary">{entry.theyCoveredMe}</div>
                                <div className="text-[10px] text-content-tertiary uppercase tracking-wide">
                                  They covered me
                                </div>
                              </div>
                              <div>
                                <div className="text-lg font-bold text-content-primary">{entry.iCoveredThem}</div>
                                <div className="text-[10px] text-content-tertiary uppercase tracking-wide">
                                  I covered them
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// -- Sub-components --

function SwapCard({
  swap,
  isIncoming,
  isOfficer,
  onAction,
  isHistorical,
}: {
  swap: SwapEntry;
  isIncoming: boolean;
  isOfficer: boolean;
  onAction: (action: 'cancel' | 'approve' | 'reject', id: number) => void;
  isHistorical?: boolean;
}) {
  const statusCfg = STATUS_CONFIG[swap.status] ?? STATUS_CONFIG.pending;

  return (
    <div className="bg-surface-raised border border-edge-default rounded-xl p-4 transition-colors duration-150">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-action-primary/15 flex items-center justify-center shrink-0">
            <ArrowLeftRight className="h-4 w-4 text-action-primary" />
          </div>
          <div>
            <div className="text-sm font-semibold text-content-primary">
              {isIncoming ? swap.requester_name : swap.target_name}
            </div>
            <div className="text-[11px] text-content-tertiary">
              {isHistorical
                ? isIncoming
                  ? 'covered your shift'
                  : 'you covered their shift'
                : isIncoming
                  ? 'wants to swap with you'
                  : 'swap request'}
            </div>
          </div>
        </div>
        <span
          className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md"
          style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}
        >
          {statusCfg.label}
        </span>
      </div>

      {/* Dates */}
      <div className="bg-surface-sunken rounded-lg p-3 mb-3 space-y-2">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-content-secondary">{isIncoming ? 'They give' : 'Giving'}</span>
          <span className="font-semibold text-content-primary">{formatDate(swap.requester_date)}</span>
        </div>
        <div className="flex items-center justify-center">
          <ChevronRight className="h-3.5 w-3.5 text-content-tertiary rotate-90" />
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-content-secondary">{isIncoming ? 'They take' : 'Taking'}</span>
          <span className="font-semibold text-content-primary">{formatDate(swap.target_date)}</span>
        </div>
      </div>

      {/* Reason */}
      {swap.reason && <div className="text-xs text-content-tertiary mb-2 italic">&ldquo;{swap.reason}&rdquo;</div>}

      {/* Meta */}
      <div className="flex items-center gap-1 text-[11px] text-content-tertiary mb-2">
        <Clock className="h-3 w-3" />
        {formatTimestamp(swap.created_at)}
      </div>

      {swap.reviewer_name && (
        <div className="text-[11px] text-content-tertiary mb-2">
          Reviewed by {swap.reviewer_name} {swap.reviewed_at ? `on ${formatTimestamp(swap.reviewed_at)}` : ''}
        </div>
      )}

      {/* Actions — only for non-historical pending swaps */}
      {!isHistorical && swap.status === 'pending' && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-edge-muted">
          {!isIncoming && (
            <button
              type="button"
              onClick={() => onAction('cancel', swap.id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold
                       text-content-secondary bg-surface-sunken border border-edge-default rounded-lg
                       cursor-pointer hover:bg-action-secondary-hover transition-colors duration-150"
            >
              <XIcon className="h-3.5 w-3.5" />
              Cancel
            </button>
          )}
          {isOfficer && (
            <>
              <button
                type="button"
                onClick={() => onAction('approve', swap.id)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold
                         text-white bg-green-600 border-none rounded-lg
                         cursor-pointer hover:bg-green-700 transition-colors duration-150"
              >
                <Check className="h-3.5 w-3.5" />
                Approve
              </button>
              <button
                type="button"
                onClick={() => onAction('reject', swap.id)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold
                         text-white bg-red-600 border-none rounded-lg
                         cursor-pointer hover:bg-red-700 transition-colors duration-150"
              >
                <XIcon className="h-3.5 w-3.5" />
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ message, action, onAction }: { message: string; action?: string; onAction?: () => void }) {
  return (
    <div className="text-center py-12 px-6">
      <div className="w-12 h-12 rounded-full bg-surface-sunken mx-auto mb-3 flex items-center justify-center">
        <ArrowLeftRight className="h-6 w-6 text-content-tertiary" />
      </div>
      <div className="text-sm text-content-tertiary mb-3">{message}</div>
      {action && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="px-4 py-2 text-xs font-semibold text-action-primary bg-action-primary/10
                   border border-action-primary/30 rounded-lg cursor-pointer
                   hover:bg-action-primary/20 transition-colors duration-150"
        >
          {action}
        </button>
      )}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-content-secondary uppercase tracking-wide mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
